import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  User, 
  Globe, 
  MapPin, 
  Building, 
  Phone,
  Save,
  Loader2,
  Mail,
  Bell,
  KeyRound,
  Shield,
  Eye,
  EyeOff
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const states = [
  { code: 'NY', name: 'New York' },
  { code: 'CA', name: 'California' },
  { code: 'TX', name: 'Texas' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'MI', name: 'Michigan' },
  { code: 'WV', name: 'West Virginia' }
];

const policyCategories = [
  { id: 'environmental', en: 'Environmental', es: 'Ambiental', fr: 'Environnemental', ar: 'بيئي', zh: '環境' },
  { id: 'voting', en: 'Voting Rights', es: 'Derechos de Voto', fr: 'Droits de Vote', ar: 'حقوق التصويت', zh: '投票權' },
  { id: 'housing', en: 'Housing', es: 'Vivienda', fr: 'Logement', ar: 'الإسكان', zh: '住房' },
  { id: 'labor', en: 'Labor', es: 'Laboral', fr: 'Travail', ar: 'العمل', zh: '勞工' },
  { id: 'health', en: 'Health', es: 'Salud', fr: 'Santé', ar: 'الصحة', zh: '健康' }
];

const jurisdictions = [
  { id: 'federal', en: 'Federal', es: 'Federal', fr: 'Fédéral', ar: 'فيدرالي', zh: '聯邦' },
  { id: 'NY', en: 'New York', es: 'Nueva York', fr: 'New York', ar: 'نيويورك', zh: '紐約' },
  { id: 'CA', en: 'California', es: 'California', fr: 'Californie', ar: 'كاليفورنيا', zh: '加州' },
  { id: 'TX', en: 'Texas', es: 'Texas', fr: 'Texas', ar: 'تكساس', zh: '德州' },
  { id: 'local', en: 'Local', es: 'Local', fr: 'Local', ar: 'محلي', zh: '地方' }
];

const Settings = () => {
  const { user, login } = useAuth();
  const { language, changeLanguage } = useLanguage();
  
  const [formData, setFormData] = useState({
    organization_name: user?.organization_name || '',
    company_name: user?.company_name || '',
    phone: user?.phone || '',
    state: user?.state || 'NY'
  });
  const [saving, setSaving] = useState(false);
  const [emailPrefs, setEmailPrefs] = useState({
    daily_digest_enabled: false,
    digest_categories: [],
    digest_jurisdictions: []
  });
  const [emailPrefsSaving, setEmailPrefsSaving] = useState(false);
  
  // Username change state
  const [usernameChangeData, setUsernameChangeData] = useState({
    new_username: '',
    password: ''
  });
  const [usernameChangeSaving, setUsernameChangeSaving] = useState(false);
  const [showUsernamePassword, setShowUsernamePassword] = useState(false);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    fetchEmailPreferences();
  }, []);

  const fetchEmailPreferences = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      const response = await axios.get(`${API}/email-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.preferences) {
        setEmailPrefs(response.data.preferences);
      }
    } catch (error) {
      console.error('Error fetching email preferences:', error);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleStateChange = (value) => {
    setFormData(prev => ({
      ...prev,
      state: value
    }));
  };

  const handleLanguageChange = async (newLang) => {
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.put(`${API}/settings/language?language=${newLang}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      changeLanguage(newLang);
      toast.success(
        newLang === 'es' ? 'Idioma cambiado' : 
        newLang === 'fr' ? 'Langue changée' :
        newLang === 'ar' ? 'تم تغيير اللغة' :
        newLang === 'zh' ? '語言已更改' :
        'Language changed'
      );
    } catch (error) {
      toast.error(language === 'es' ? 'Error al cambiar idioma' : 'Failed to change language');
    }
  };

  const handleUsernameChangeInput = (e) => {
    setUsernameChangeData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const changeUsername = async () => {
    // Validate
    if (!usernameChangeData.new_username) {
      toast.error(language === 'es' ? 'Ingresa un nombre de usuario' : 'Enter a username');
      return;
    }
    if (usernameChangeData.new_username.length < 3) {
      toast.error(language === 'es' ? 'El nombre de usuario debe tener al menos 3 caracteres' : 'Username must be at least 3 characters');
      return;
    }
    if (!usernameChangeData.password) {
      toast.error(language === 'es' ? 'Ingresa tu contraseña' : 'Enter your password');
      return;
    }
    
    setUsernameChangeSaving(true);
    try {
      const token = localStorage.getItem('polibot_token');
      const response = await axios.post(`${API}/auth/change-username`, {
        new_username: usernameChangeData.new_username,
        password: usernameChangeData.password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local storage with new username
      const updatedUser = { ...user, username: response.data.new_username };
      localStorage.setItem('polibot_user', JSON.stringify(updatedUser));
      login(updatedUser, token);
      
      toast.success(language === 'es' ? '¡Nombre de usuario actualizado!' : 'Username updated successfully!');
      setUsernameChangeData({ new_username: '', password: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 
        (language === 'es' ? 'Error al cambiar nombre de usuario' : 'Failed to change username');
      toast.error(message);
    } finally {
      setUsernameChangeSaving(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.put(`${API}/settings/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const updatedUser = { ...user, ...formData };
      localStorage.setItem('polibot_user', JSON.stringify(updatedUser));
      login(updatedUser, token);
      
      toast.success(language === 'es' ? 'Perfil actualizado' : 'Profile updated');
    } catch (error) {
      toast.error(language === 'es' ? 'Error al guardar' : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveEmailPreferences = async () => {
    setEmailPrefsSaving(true);
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.put(`${API}/email-preferences`, emailPrefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(
        language === 'es' ? 'Preferencias de email guardadas' :
        language === 'fr' ? 'Préférences email enregistrées' :
        'Email preferences saved'
      );
    } catch (error) {
      toast.error(language === 'es' ? 'Error al guardar' : 'Failed to save');
    } finally {
      setEmailPrefsSaving(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setEmailPrefs(prev => ({
      ...prev,
      digest_categories: prev.digest_categories.includes(categoryId)
        ? prev.digest_categories.filter(c => c !== categoryId)
        : [...prev.digest_categories, categoryId]
    }));
  };

  const toggleJurisdiction = (jurisdictionId) => {
    setEmailPrefs(prev => ({
      ...prev,
      digest_jurisdictions: prev.digest_jurisdictions.includes(jurisdictionId)
        ? prev.digest_jurisdictions.filter(j => j !== jurisdictionId)
        : [...prev.digest_jurisdictions, jurisdictionId]
    }));
  };

  const handlePasswordInputChange = (e) => {
    setPasswordData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const changePassword = async () => {
    // Validate passwords
    if (!passwordData.current_password) {
      toast.error(language === 'es' ? 'Ingresa tu contraseña actual' : 'Enter your current password');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error(language === 'es' ? 'La nueva contraseña debe tener al menos 6 caracteres' : 'New password must be at least 6 characters');
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error(language === 'es' ? 'Las contraseñas no coinciden' : 'Passwords do not match');
      return;
    }
    
    setPasswordSaving(true);
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(language === 'es' ? '¡Contraseña actualizada!' : 'Password updated successfully!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 
        (language === 'es' ? 'Error al cambiar contraseña' : 'Failed to change password');
      toast.error(message);
    } finally {
      setPasswordSaving(false);
    }
  };

  const getLabel = (item) => item[language] || item.en;

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
          {language === 'es' ? 'Configuración' : 
           language === 'fr' ? 'Paramètres' :
           language === 'ar' ? 'الإعدادات' :
           language === 'zh' ? '設置' :
           'Settings'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {language === 'es' ? 'Administra tu perfil y preferencias de cuenta' :
           language === 'fr' ? 'Gérez votre profil et vos préférences' :
           language === 'ar' ? 'إدارة ملفك الشخصي وتفضيلات الحساب' :
           language === 'zh' ? '管理您的個人資料和帳戶偏好設定' :
           'Manage your profile and account preferences'}
        </p>
      </div>

      {/* Profile Settings */}
      <Card data-testid="profile-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            {language === 'es' ? 'Perfil' : 
             language === 'fr' ? 'Profil' :
             language === 'ar' ? 'الملف الشخصي' :
             language === 'zh' ? '個人資料' :
             'Profile'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {user?.user_type === 'corporate_esg' ? (
            <div className="space-y-2">
              <Label htmlFor="company_name">
                <Building className="h-4 w-4 inline mr-2" />
                {language === 'es' ? 'Nombre de Empresa' : 'Company Name'}
              </Label>
              <Input
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                data-testid="company-name-input"
              />
            </div>
          ) : user?.user_type === 'community_org' ? (
            <div className="space-y-2">
              <Label htmlFor="organization_name">
                <Building className="h-4 w-4 inline mr-2" />
                {language === 'es' ? 'Nombre de Organización' : 'Organization Name'}
              </Label>
              <Input
                id="organization_name"
                name="organization_name"
                value={formData.organization_name}
                onChange={handleChange}
                data-testid="org-name-input"
              />
            </div>
          ) : null}
          
          <div className="space-y-2">
            <Label htmlFor="phone">
              <Phone className="h-4 w-4 inline mr-2" />
              {language === 'es' ? 'Teléfono' : 
               language === 'fr' ? 'Téléphone' :
               language === 'ar' ? 'الهاتف' :
               language === 'zh' ? '電話' :
               'Phone'}
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+1 (555) 000-0000"
              data-testid="phone-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label>
              <MapPin className="h-4 w-4 inline mr-2" />
              {language === 'es' ? 'Estado' : 
               language === 'fr' ? 'État' :
               language === 'ar' ? 'الولاية' :
               language === 'zh' ? '州' :
               'State'}
            </Label>
            <Select value={formData.state} onValueChange={handleStateChange}>
              <SelectTrigger data-testid="state-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {states.map(state => (
                  <SelectItem key={state.code} value={state.code}>
                    {state.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button onClick={saveProfile} disabled={saving} data-testid="save-profile-btn">
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {language === 'es' ? 'Guardando...' : 'Saving...'}
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {language === 'es' ? 'Guardar' : 
                 language === 'fr' ? 'Sauvegarder' :
                 language === 'ar' ? 'حفظ' :
                 language === 'zh' ? '儲存' :
                 'Save Changes'}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Account / Username Change */}
      <Card data-testid="account-username-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            {language === 'es' ? 'Cambiar Nombre de Usuario' : 
             language === 'fr' ? 'Changer le Nom d\'Utilisateur' :
             language === 'ar' ? 'تغيير اسم المستخدم' :
             language === 'zh' ? '更改用戶名' :
             'Change Username'}
          </CardTitle>
          <CardDescription>
            {language === 'es' ? 'Actualiza tu nombre de usuario (requiere contraseña)' :
             language === 'fr' ? 'Mettez à jour votre nom d\'utilisateur (mot de passe requis)' :
             'Update your display username (password required)'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>
              {language === 'es' ? 'Usuario Actual' : 
               language === 'fr' ? 'Nom d\'Utilisateur Actuel' :
               'Current Username'}
            </Label>
            <Input 
              value={user?.username || '(not set)'} 
              disabled 
              className="bg-muted"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="new_username">
              {language === 'es' ? 'Nuevo Nombre de Usuario' : 
               language === 'fr' ? 'Nouveau Nom d\'Utilisateur' :
               'New Username'}
            </Label>
            <Input
              id="new_username"
              name="new_username"
              type="text"
              value={usernameChangeData.new_username}
              onChange={handleUsernameChangeInput}
              placeholder="my_username"
              data-testid="new-username-input"
            />
            <p className="text-xs text-muted-foreground">
              {language === 'es' ? '3-30 caracteres' : '3-30 characters'}
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="username_password">
              {language === 'es' ? 'Confirmar con Contraseña' : 
               language === 'fr' ? 'Confirmer avec Mot de Passe' :
               'Confirm with Password'}
            </Label>
            <div className="relative">
              <Input
                id="username_password"
                name="password"
                type={showUsernamePassword ? 'text' : 'password'}
                value={usernameChangeData.password}
                onChange={handleUsernameChangeInput}
                placeholder="••••••••"
                data-testid="username-password-input"
              />
              <button
                type="button"
                onClick={() => setShowUsernamePassword(!showUsernamePassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showUsernamePassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          <Button 
            onClick={changeUsername} 
            disabled={usernameChangeSaving}
            data-testid="change-username-btn"
          >
            {usernameChangeSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {language === 'es' ? 'Actualizando...' : 'Updating...'}
              </>
            ) : (
              <>
                <User className="mr-2 h-4 w-4" />
                {language === 'es' ? 'Cambiar Usuario' : 
                 language === 'fr' ? 'Changer le Nom' :
                 'Change Username'}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Email Notifications */}
      <Card data-testid="email-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            {language === 'es' ? 'Notificaciones por Email' : 
             language === 'fr' ? 'Notifications par Email' :
             language === 'ar' ? 'إشعارات البريد الإلكتروني' :
             language === 'zh' ? '電子郵件通知' :
             'Email Notifications'}
          </CardTitle>
          <CardDescription>
            {language === 'es' ? 'Recibe resúmenes diarios de políticas a las 7:30 AM EST' :
             language === 'fr' ? 'Recevez des résumés quotidiens à 7h30 EST' :
             'Receive daily policy digests at 7:30 AM EST'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">
                {language === 'es' ? 'Resumen Diario' : 
                 language === 'fr' ? 'Résumé Quotidien' :
                 language === 'ar' ? 'ملخص يومي' :
                 language === 'zh' ? '每日摘要' :
                 'Daily Digest'}
              </Label>
              <p className="text-sm text-muted-foreground">
                {language === 'es' ? 'Recibe un email diario con las políticas relevantes' :
                 'Get a daily email with relevant policy updates'}
              </p>
            </div>
            <Switch
              checked={emailPrefs.daily_digest_enabled}
              onCheckedChange={(checked) => setEmailPrefs(prev => ({ ...prev, daily_digest_enabled: checked }))}
              data-testid="daily-digest-switch"
            />
          </div>

          {emailPrefs.daily_digest_enabled && (
            <div className="space-y-4 pt-4 border-t animate-fade-in">
              <div>
                <Label className="text-sm font-medium mb-3 block">
                  {language === 'es' ? 'Categorías de Interés' : 
                   language === 'fr' ? 'Catégories d\'Intérêt' :
                   'Categories of Interest'}
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {policyCategories.map(cat => (
                    <div key={cat.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`cat-${cat.id}`}
                        checked={emailPrefs.digest_categories.includes(cat.id)}
                        onCheckedChange={() => toggleCategory(cat.id)}
                      />
                      <Label htmlFor={`cat-${cat.id}`} className="text-sm cursor-pointer">
                        {getLabel(cat)}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium mb-3 block">
                  {language === 'es' ? 'Jurisdicciones' : 
                   language === 'fr' ? 'Juridictions' :
                   'Jurisdictions'}
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {jurisdictions.map(jur => (
                    <div key={jur.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`jur-${jur.id}`}
                        checked={emailPrefs.digest_jurisdictions.includes(jur.id)}
                        onCheckedChange={() => toggleJurisdiction(jur.id)}
                      />
                      <Label htmlFor={`jur-${jur.id}`} className="text-sm cursor-pointer">
                        {getLabel(jur)}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <Button onClick={saveEmailPreferences} disabled={emailPrefsSaving} data-testid="save-email-btn">
            {emailPrefsSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Bell className="mr-2 h-4 w-4" />
            )}
            {language === 'es' ? 'Guardar Preferencias' : 'Save Preferences'}
          </Button>
        </CardContent>
      </Card>

      {/* Language Settings */}
      <Card data-testid="language-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            {language === 'es' ? 'Idioma' : 
             language === 'fr' ? 'Langue' :
             language === 'ar' ? 'اللغة' :
             language === 'zh' ? '語言' :
             'Language'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={language === 'en' ? 'default' : 'outline'}
              onClick={() => handleLanguageChange('en')}
              size="sm"
            >
              English
            </Button>
            <Button
              variant={language === 'es' ? 'default' : 'outline'}
              onClick={() => handleLanguageChange('es')}
              size="sm"
            >
              Español
            </Button>
            <Button
              variant={language === 'fr' ? 'default' : 'outline'}
              onClick={() => handleLanguageChange('fr')}
              size="sm"
            >
              Français
            </Button>
            <Button
              variant={language === 'ar' ? 'default' : 'outline'}
              onClick={() => handleLanguageChange('ar')}
              size="sm"
            >
              العربية
            </Button>
            <Button
              variant={language === 'zh' ? 'default' : 'outline'}
              onClick={() => handleLanguageChange('zh')}
              size="sm"
            >
              繁體中文
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Security / Password Change */}
      <Card data-testid="security-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            {language === 'es' ? 'Seguridad' : 
             language === 'fr' ? 'Sécurité' :
             language === 'ar' ? 'الأمان' :
             language === 'zh' ? '安全' :
             'Security'}
          </CardTitle>
          <CardDescription>
            {language === 'es' ? 'Actualiza tu contraseña' :
             language === 'fr' ? 'Mettez à jour votre mot de passe' :
             'Update your password'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current_password">
              {language === 'es' ? 'Contraseña Actual' : 
               language === 'fr' ? 'Mot de passe actuel' :
               'Current Password'}
            </Label>
            <div className="relative">
              <Input
                id="current_password"
                name="current_password"
                type={showCurrentPassword ? 'text' : 'password'}
                value={passwordData.current_password}
                onChange={handlePasswordInputChange}
                placeholder="••••••••"
                data-testid="current-password-input"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="new_password">
              {language === 'es' ? 'Nueva Contraseña' : 
               language === 'fr' ? 'Nouveau mot de passe' :
               'New Password'}
            </Label>
            <div className="relative">
              <Input
                id="new_password"
                name="new_password"
                type={showNewPassword ? 'text' : 'password'}
                value={passwordData.new_password}
                onChange={handlePasswordInputChange}
                placeholder="••••••••"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirm_password">
              {language === 'es' ? 'Confirmar Nueva Contraseña' : 
               language === 'fr' ? 'Confirmer le nouveau mot de passe' :
               'Confirm New Password'}
            </Label>
            <Input
              id="confirm_password"
              name="confirm_password"
              type="password"
              value={passwordData.confirm_password}
              onChange={handlePasswordInputChange}
              placeholder="••••••••"
              data-testid="confirm-new-password-input"
            />
          </div>
          
          <Button 
            onClick={changePassword} 
            disabled={passwordSaving}
            data-testid="change-password-btn"
          >
            {passwordSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {language === 'es' ? 'Actualizando...' : 'Updating...'}
              </>
            ) : (
              <>
                <KeyRound className="mr-2 h-4 w-4" />
                {language === 'es' ? 'Cambiar Contraseña' : 
                 language === 'fr' ? 'Changer le mot de passe' :
                 'Change Password'}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card data-testid="account-info">
        <CardHeader>
          <CardTitle>
            {language === 'es' ? 'Información de Cuenta' : 
             language === 'fr' ? 'Informations du Compte' :
             language === 'ar' ? 'معلومات الحساب' :
             language === 'zh' ? '帳戶資訊' :
             'Account Information'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <span className="text-muted-foreground">
              {language === 'es' ? 'Correo Electrónico' : 'Email'}
            </span>
            <span className="font-medium">{user?.email}</span>
          </div>
          <Separator />
          <div className="flex items-center justify-between py-2">
            <span className="text-muted-foreground">
              {language === 'es' ? 'Tipo de Cuenta' : 'Account Type'}
            </span>
            <span className="font-medium capitalize">
              {user?.user_type?.replace('_', ' ') || 'Community Member'}
            </span>
          </div>
          <Separator />
          <div className="flex items-center justify-between py-2">
            <span className="text-muted-foreground">
              {language === 'es' ? 'Cuenta Creada' : 'Account Created'}
            </span>
            <span className="font-medium">
              {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
